import React, { useCallback, useEffect, useMemo } from 'react';

import CustomReportParams from '../customReports/customReportParams.component';
import { useCustomReportParamsOptions } from '../../shared/queries/customReports';
import { getParams } from './helpers';
import {
  isReportSet,
  QueryParams,
  CustomReportParamDefinition,
  ParamValues,
} from '../../shared/types/customReports';
import { CustomReportDefinition } from '../../shared/types/batchReports';

type UniqueParam = CustomReportParamDefinition & {
  id: string,
}

type CustomReportRowParamsProps = {
  reportDefinition: CustomReportDefinition,
  reportParamValues: ParamValues,
  updateReportParamValues: (itemName: string, newParameters: QueryParams) => void,
  archivalFlag: boolean,
  restrictBatchDefaults: boolean,
  userRole:string,
  editable:boolean
}

export const CustomReportRowParams = ({
  reportDefinition,
  reportParamValues,
  updateReportParamValues,
  archivalFlag,
  restrictBatchDefaults,
  userRole,
  editable,
}: CustomReportRowParamsProps) => {
  const {
    uniqueParams,
    mapping,
  } = useMemo(() => getParams(reportDefinition),
    [reportDefinition]);

  const mappedParamValuesForAPI = useMemo(() => {
    if (!reportParamValues || !isReportSet(reportDefinition)) {
      return reportParamValues || {};
    }
    return uniqueParams.reduce((acc: ParamValues, param: UniqueParam) => {
      acc[param.name] = reportParamValues[param.id] || '';
      return acc;
    }, {} as ParamValues);
  }, [reportParamValues, reportDefinition, uniqueParams]);

  const {
    data: customReportsParamsOptions,
    isFetching: isLoadingCustomReportParamsOptions,
    refetch: refetchCustomReportsParamsOptions,
  } = useCustomReportParamsOptions(
    reportDefinition.reportSetId,
    mapping,
    mappedParamValuesForAPI,
    Boolean(!reportParamValues)
  );

  const inputParameters = Object.values(reportParamValues || {}).join(',');

  // refetch only when inputParameters change
  useEffect(() => {
    refetchCustomReportsParamsOptions();
  }, [refetchCustomReportsParamsOptions, inputParameters]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = useCallback(newParamValues => {
    let mappedValues = newParamValues;

    if (isReportSet(reportDefinition) && mapping) {
      mappedValues = {};
      Object.keys(newParamValues).forEach(paramId => {
        const value = newParamValues[paramId];

        let paramName: string | null = null;
        for (const reportId of Object.keys(mapping)) {
          const reportMapping = mapping[reportId];
          if (reportMapping) {
            const foundEntry = Object.entries(reportMapping).find(([, id]) => id === paramId);
            if (foundEntry) {
              paramName = foundEntry[0];
              break;
            }
          }
        }

        if (paramName) {
          Object.keys(mapping).forEach(reportId => {
            const reportMapping = mapping[reportId];
            if (reportMapping && paramName) {
              const mappedId = reportMapping[paramName];
              if (mappedId) {
                mappedValues[mappedId] = value;
              }
            }
          });
        }
      });
    }

    const mergedParamValues = {
      ...(reportParamValues ? reportParamValues : {}),
      ...mappedValues,
    };
    updateReportParamValues(reportDefinition.reportSetId, mergedParamValues);
  }, [reportDefinition, reportParamValues, updateReportParamValues, mapping]);

  return (
    <CustomReportParams
      updateQueryParams={update}
      customReportsParamsOptions={customReportsParamsOptions}
      params={uniqueParams}
      queryParams={reportParamValues || {}}
      isLoading={isLoadingCustomReportParamsOptions || !reportParamValues}
      archivalFlag={archivalFlag}
      restrictBatchDefaults={restrictBatchDefaults}
      userRole={userRole}
      editable={editable}
    />
  );
};
